import {
  MIN_TARGET_SPEND_CENTS,
  type Adventure,
  type BucketComposition,
  type BucketMode,
  type TasteId,
} from './bucket.types'
import type { DeliverySlotTemplate } from '../../data/delivery'

export type BucketStage =
  | 'eligibility'
  | 'recipient'
  | 'preferences'
  | 'adventure'
  | 'budget'
  | 'revealed'
  | 'surprise'
  | 'demo_complete'

export interface BucketSession {
  readonly stage: BucketStage
  readonly status:
    | 'active'
    | 'zone_unconfirmed'
    | 'allergen_review'
    | 'revealed'
    | 'demo_complete'
  readonly mode: BucketMode | null
  readonly eligibility: {
    readonly postcode: string
    readonly status: 'idle' | 'eligible' | 'unconfirmed' | 'invalid'
  }
  readonly preferences: {
    readonly likes: readonly TasteId[]
    readonly hardNos: readonly TasteId[]
    readonly allergenReviewRequired: boolean
    readonly adventure: Adventure
    readonly targetSpendCents: number
  }
  readonly composition: BucketComposition | null
  readonly cadence: 'once' | 'weekly' | 'fortnightly' | null
  readonly deliverySlotId: DeliverySlotTemplate['id'] | null
  readonly executionMode: 'simulation'
}

export type BucketAction =
  | { readonly type: 'postcode_submitted'; readonly postcode: string }
  | { readonly type: 'mode_selected'; readonly mode: BucketMode }
  | { readonly type: 'likes_updated'; readonly likes: readonly TasteId[] }
  | { readonly type: 'hard_nos_updated'; readonly hardNos: readonly TasteId[] }
  | { readonly type: 'allergen_concern_recorded' }
  | { readonly type: 'preferences_confirmed' }
  | { readonly type: 'adventure_selected'; readonly adventure: Adventure }
  | {
      readonly type: 'budget_selected'
      readonly targetSpendCents: number
    }
  | {
      readonly type: 'composition_revealed'
      readonly composition: BucketComposition
    }
  | {
      readonly type: 'cadence_selected'
      readonly cadence: 'once' | 'weekly' | 'fortnightly'
    }
  | {
      readonly type: 'delivery_selected'
      readonly deliverySlotId: DeliverySlotTemplate['id']
    }
  | { readonly type: 'plan_added' }
  | { readonly type: 'plan_edit_requested' }
  | { readonly type: 'reset' }

export function createInitialBucketSession(): BucketSession {
  return {
    stage: 'eligibility',
    status: 'active',
    mode: null,
    eligibility: { postcode: '', status: 'idle' },
    preferences: {
      likes: [],
      hardNos: [],
      allergenReviewRequired: false,
      adventure: 'one_wildcard',
      targetSpendCents: 3500,
    },
    composition: null,
    cadence: null,
    deliverySlotId: null,
    executionMode: 'simulation',
  }
}

function assertValidTargetSpend(targetSpendCents: number): void {
  if (
    !Number.isSafeInteger(targetSpendCents) ||
    targetSpendCents < MIN_TARGET_SPEND_CENTS
  ) {
    throw new RangeError(
      `targetSpendCents must be a finite integer of at least ${MIN_TARGET_SPEND_CENTS}`,
    )
  }
}

export function bucketReducer(
  state: BucketSession,
  action: BucketAction,
): BucketSession {
  switch (action.type) {
    case 'postcode_submitted': {
      if (state.stage !== 'eligibility') return state

      const valid = /^\d{4}$/.test(action.postcode)
      const eligible =
        action.postcode === '4680' || action.postcode === '4700'

      return {
        ...state,
        stage: valid ? 'recipient' : 'eligibility',
        status: valid && !eligible ? 'zone_unconfirmed' : 'active',
        eligibility: {
          postcode: action.postcode,
          status: !valid ? 'invalid' : eligible ? 'eligible' : 'unconfirmed',
        },
      }
    }
    case 'mode_selected':
      if (state.stage !== 'recipient') return state
      return { ...state, mode: action.mode, stage: 'preferences' }
    case 'likes_updated':
      if (state.stage !== 'preferences') return state
      return {
        ...state,
        preferences: { ...state.preferences, likes: [...action.likes] },
      }
    case 'hard_nos_updated':
      if (state.stage !== 'preferences') return state
      return {
        ...state,
        preferences: { ...state.preferences, hardNos: [...action.hardNos] },
      }
    case 'allergen_concern_recorded':
      if (state.stage !== 'preferences') return state
      return {
        ...state,
        status: 'allergen_review',
        preferences: {
          ...state.preferences,
          allergenReviewRequired: true,
        },
      }
    case 'preferences_confirmed':
      if (
        state.stage !== 'preferences' ||
        (state.status !== 'active' && state.status !== 'zone_unconfirmed')
      ) {
        return state
      }
      return { ...state, stage: 'adventure' }
    case 'adventure_selected':
      if (
        state.stage !== 'adventure' ||
        (state.status !== 'active' && state.status !== 'zone_unconfirmed')
      ) {
        return state
      }
      return {
        ...state,
        stage: 'budget',
        preferences: {
          ...state.preferences,
          adventure: action.adventure,
        },
      }
    case 'budget_selected':
      if (
        state.stage !== 'budget' ||
        (state.status !== 'active' && state.status !== 'zone_unconfirmed')
      ) {
        return state
      }
      assertValidTargetSpend(action.targetSpendCents)
      return {
        ...state,
        preferences: {
          ...state.preferences,
          targetSpendCents: action.targetSpendCents,
        },
      }
    case 'composition_revealed': {
      if (
        state.stage !== 'budget' ||
        (state.status !== 'active' && state.status !== 'zone_unconfirmed')
      ) {
        return state
      }

      return {
        ...state,
        stage: 'revealed',
        status: 'revealed',
        composition: {
          ...action.composition,
          lines: action.composition.lines.map((line) => ({ ...line })),
        },
      }
    }
    case 'cadence_selected': {
      if (state.stage !== 'revealed' || state.status !== 'revealed') {
        return state
      }

      const isRecurring = action.cadence !== 'once'
      if (isRecurring && state.mode === 'gift') return state

      return {
        ...state,
        stage: isRecurring ? 'surprise' : 'demo_complete',
        status: isRecurring ? 'revealed' : 'demo_complete',
        cadence: action.cadence,
        deliverySlotId: null,
      }
    }
    case 'delivery_selected':
      if (
        state.stage !== 'surprise' ||
        (state.cadence !== 'weekly' && state.cadence !== 'fortnightly')
      ) {
        return state
      }
      return { ...state, deliverySlotId: action.deliverySlotId }
    case 'plan_added':
      if (
        state.stage !== 'surprise' ||
        !state.deliverySlotId ||
        (state.cadence !== 'weekly' && state.cadence !== 'fortnightly')
      ) {
        return state
      }
      return { ...state, stage: 'demo_complete', status: 'demo_complete' }
    case 'plan_edit_requested':
      if (
        (state.stage !== 'surprise' && state.stage !== 'demo_complete') ||
        !state.composition
      ) {
        return state
      }
      return {
        ...state,
        stage: 'revealed',
        status: 'revealed',
        cadence: null,
        deliverySlotId: null,
      }
    case 'reset':
      return createInitialBucketSession()
  }
}
